"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.WorkflowValidator = void 0;
const sortTypeMap = {
    '': 0,
    'no-plugin': 1,
    service: 2,
    'transition-condition': 3
};
/**
 * Validate Workflow config and plugins.
 */
class WorkflowValidator {
    constructor(servicePlugins = [], transitionConditionPlugins = []) {
        this.servicePlugins = servicePlugins;
        this.transitionConditionPlugins = transitionConditionPlugins;
    }
    runChecks(args, context) {
        var _a, _b, _c, _d;
        return __awaiter(this, void 0, void 0, function* () {
            const results = [];
            if (args.type === '' || args.type === 'no-plugin') {
                results.push({ severity: 'warning', message: 'Plugin not found.' });
                return { isValid: true, hasWarning: false, results };
            }
            // validate
            const validate = args.type === 'service'
                ? args.plugin.validate(args.invoke)
                : args.plugin.validate(args.condition);
            results.push(validate
                ? { severity: 'success', message: 'Configuration check passed.' }
                : { severity: 'error', message: 'Configuration check failed.' });
            // validate import
            context = Object.assign(Object.assign({}, context), { path: args.path, stateNode: args.stateNode });
            try {
                const validateImport = args.type === 'service'
                    ? yield ((_b = (_a = args.plugin).validateImport) === null || _b === void 0 ? void 0 : _b.call(_a, args.invoke, context))
                    : yield ((_d = (_c = args.plugin).validateImport) === null || _d === void 0 ? void 0 : _d.call(_c, args.condition, context));
                const didValidate = !!args.plugin.validateImport;
                results.push(validateImport || {
                    severity: didValidate ? 'success' : 'info',
                    message: `Import validation ${didValidate ? 'passed' : 'skipped'}.`
                });
            }
            catch (error) {
                results.push({
                    severity: 'error',
                    message: error instanceof Error ? error.message : 'An error occurred.'
                });
            }
            return {
                results,
                isValid: results.every(result => result.severity !== 'error'),
                hasWarning: results.some(result => result.severity === 'warning')
            };
        });
    }
    validate(stateNode, context, includeValid = false, isRoot = true) {
        return __awaiter(this, void 0, void 0, function* () {
            let didTraverse = false;
            const result = {
                isValid: true,
                hasWarning: false,
                plugins: []
            };
            yield this.traverse(stateNode, (args) => __awaiter(this, void 0, void 0, function* () {
                didTraverse = true;
                const check = yield this.runChecks(args, context);
                const results = includeValid
                    ? check.results
                    : check.results.filter(item => {
                        return ['error', 'warning'].includes(item.severity);
                    });
                if (!check.isValid) {
                    result.isValid = false;
                }
                if (check.hasWarning) {
                    result.hasWarning = true;
                }
                if (includeValid || results.length > 0) {
                    result.plugins.push(Object.assign(Object.assign({}, args), { results, isValid: check.isValid, hasWarning: check.hasWarning }));
                }
            }));
            // if no invokes or transitions were checked,
            // add empty to include stateNode in results
            if (!didTraverse && !isRoot && includeValid && stateNode) {
                result.plugins.push({
                    type: '',
                    path: '',
                    stateNode,
                    results: [],
                    isValid: true,
                    hasWarning: false
                });
            }
            // validate state children
            for (const state of (stateNode === null || stateNode === void 0 ? void 0 : stateNode.states) || []) {
                const stateResult = yield this.validate(state, context, includeValid, false);
                result.plugins.push(...stateResult.plugins);
                if (!stateResult.isValid) {
                    result.isValid = false;
                }
                if (stateResult.hasWarning) {
                    result.hasWarning = true;
                }
            }
            return result;
        });
    }
    // go through all invokes and event transition conditions
    traverse(stateNode, handle) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!stateNode) {
                return;
            }
            // validate invokes
            const invokes = Array.isArray(stateNode.invoke)
                ? stateNode.invoke
                : stateNode.invoke
                    ? [stateNode.invoke]
                    : [];
            for (const [index, invoke] of Array.from(invokes.entries())) {
                const path = `invoke[${index}]`;
                const plugin = this.servicePlugins.find(plugin => plugin.src === invoke.src);
                if (plugin) {
                    yield handle({ type: 'service', path, plugin, stateNode, invoke });
                }
                else {
                    yield handle({ type: 'no-plugin', path, stateNode });
                }
            }
            // flatten events->transitions->conditions
            const events = Array.isArray(stateNode.events) ? stateNode.events : [];
            const flatEvents = events.reduce((arr, event, eventIndex) => {
                for (const [transitionIndex, transition] of Array.from((event.transitions || []).entries())) {
                    const conditions = Array.isArray(transition.conditions)
                        ? transition.conditions
                        : transition.conditions
                            ? [transition.conditions]
                            : [];
                    arr.push(...conditions.map((condition, conditionIndex) => {
                        const path = [
                            `events[${eventIndex}]`,
                            `transitions[${transitionIndex}]`,
                            `conditions[${conditionIndex}]`
                        ].join('.');
                        return { path, event, transition, condition };
                    }));
                }
                return arr;
            }, []);
            // validate event transitions
            for (const flatEvent of flatEvents) {
                const plugin = this.transitionConditionPlugins.find(plugin => plugin.type === flatEvent.condition.type);
                if (plugin) {
                    yield handle(Object.assign({ type: 'transition-condition', plugin,
                        stateNode }, flatEvent));
                }
                else {
                    yield handle({ type: 'no-plugin', path: flatEvent.path, stateNode });
                }
            }
        });
    }
    groupByState(result, includeValid = false) {
        const results = [];
        for (const plugin of result.plugins) {
            const { stateNode } = plugin;
            const index = results.findIndex(item => item.stateNode.name === stateNode.name);
            const item = index > -1
                ? results[index]
                : { isValid: true, hasWarning: false, stateNode, plugins: [] };
            if (index === -1) {
                results.push(item);
            }
            if (!plugin.isValid) {
                item.isValid = false;
            }
            if (plugin.hasWarning) {
                item.hasWarning = true;
            }
            const pluginResults = includeValid
                ? plugin.results
                : plugin.results.filter(item => {
                    return ['error', 'warning'].includes(item.severity);
                });
            if (includeValid || pluginResults.length > 0) {
                item.plugins.push(Object.assign(Object.assign({}, plugin), { results: pluginResults }));
            }
        }
        for (const item of results) {
            item.plugins.sort((a, b) => sortTypeMap[a.type] - sortTypeMap[b.type]);
        }
        return includeValid
            ? results
            : results.filter(item => {
                return item.plugins.some(plugin => plugin.results.length > 0);
            });
    }
}
exports.WorkflowValidator = WorkflowValidator;
//# sourceMappingURL=WorkflowValidator.js.map